import { Button } from '@design-system/components/button/button';
import { IconButton } from '@design-system/components/button/iconButton';
import { PlainModal } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import {
  faCalendar,
  faCalendarClock,
} from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import moment from 'moment-timezone';
import { EditWorkoutVM } from '../view-models/workoutEdit.viewModel';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import {
  SchedulePicker,
  timezoneValues,
  useJoinScheduleGroup,
} from '@client/new/features/schedule-groups';

export {
  dayTimeValues,
  timezoneOptions,
  timezoneValues,
} from '@client/new/features/schedule-groups';

const DEFAULT_TIMEZONE = timezoneValues[0];

export const Schedule = ({
  dispatch,
  watch,
  workoutId,
  disabled,
  loading,
}: Pick<EditWorkoutVM, 'dispatch' | 'watch' | 'workoutId'> & {
  disabled?: boolean;
  loading?: boolean;
}) => {
  const timezone = watch('timeZone');
  const publishedAt = watch('publishedAt');
  const [scheduleBeforeOpen, setScheduleBeforeOpen] = useState<
    { publishedAt?: string; timeZone?: string } | undefined
  >();
  const open = scheduleBeforeOpen !== undefined;
  const setPublishedAt = (value: string) => dispatch('setPublishedAt', value);
  const scheduleGroup = useJoinScheduleGroup({
    influencerId: useInfluencerId(),
    programId: watch('programId'),
    workoutId,
    publishedAt,
    setPublishedAt,
  });
  const selectedTz = timezone || DEFAULT_TIMEZONE;
  const publishedDate = moment.tz(publishedAt, selectedTz).format('L');

  const openModal = () => {
    setScheduleBeforeOpen({ publishedAt, timeZone: timezone });
    if (!timezone) {
      dispatch('setTimeZone', DEFAULT_TIMEZONE);
    }
    scheduleGroup.reset();
  };

  const close = () => setScheduleBeforeOpen(undefined);

  const schedule = () => {
    close();
    dispatch('publish');
  };

  const cancel = () => {
    if (scheduleBeforeOpen) dispatch('restoreSchedule', scheduleBeforeOpen);
    close();
  };

  return (
    <>
      <Buttons
        publishedDate={publishedDate}
        setOpen={openModal}
        isDateSet={!!publishedAt}
        disabled={disabled}
        loading={loading}
      />
      <PlainModal
        isOpen={open}
        onClose={close}
        modalClassName={
          scheduleGroup.times.length > 0 ? 'max-w-[528px]' : 'max-w-[362px]'
        }
      >
        <div className="flex flex-col gap-6">
          <Typography>
            Select date & time when your workout will be published
          </Typography>
          <SchedulePicker
            publishedAt={publishedAt}
            timeZone={selectedTz}
            onChange={setPublishedAt}
            onTimeZoneChange={(value) => dispatch('setTimeZone', value)}
            times={scheduleGroup.times}
            joined={scheduleGroup.joined}
            onJoin={scheduleGroup.join}
            onClear={scheduleGroup.clear}
            programName={scheduleGroup.programName}
            joiningCount={1}
          />
          <div className="flex items-center justify-between">
            <Button variant="text" color="neutral" onClick={cancel}>
              Cancel
            </Button>
            <Button
              color="neutral"
              onClick={schedule}
              disabled={!!publishedAt && Date.parse(publishedAt) <= Date.now()}
            >
              Schedule
            </Button>
          </div>
        </div>
      </PlainModal>
    </>
  );
};

const Buttons = ({
  publishedDate,
  setOpen,
  isDateSet,
  disabled,
  loading,
}: {
  publishedDate: string;
  setOpen: () => void;
  isDateSet: boolean;
  disabled?: boolean;
  loading?: boolean;
}) => {
  if (isDateSet) {
    return (
      <Button
        variant="text"
        leftIcon={faCalendar}
        onClick={setOpen}
        testId="scheduleWorkoutDate"
        loading={loading}
        disabled={disabled || loading}
      >
        {publishedDate}
      </Button>
    );
  }
  return (
    <IconButton
      color="neutral"
      icon={faCalendarClock}
      onClick={setOpen}
      data-testid="scheduleWorkout"
      disabled={disabled || loading}
    />
  );
};
